<template>
    <div class="wrapping" ref="tableWrappingRef">
        <!-- *********************************************
		*                     Shadows                    *
		********************************************** -->
        <!-- Left -->
        <div v-if="hasLeftShadow && !noScrollNeeded"
		class="shadow-left"
		:class="{'inside-detail': insideDetail, 'standalone': standAlone}"
		:style="{'height': `${tableHeight}px`}"/>
        <!-- Right -->
        <div
		v-if="hasRightShadow && !noScrollNeeded"
		class="shadow-right"
		:class="{'inside-detail': insideDetail, 'standalone': standAlone}"
		:style="{'height': `${tableHeight}px`}"/>
		<!-- *********************************************
		*                                                *
		*               Table & Fake Scroll              *
		*                                                *
		********************************************** -->
        <div
		v-dragscroll
        v-on:dragscrollstart="grabing = true"
		v-on:dragscrollend="grabing = false"
        ref="tableContainerWrapperRef"
        class="table-wrapper-for-scroll inside-step-container"
		:class="{'grabing': grabing}"
        @scroll="trueScrollHandler">
            <div
			ref="tableContainer"
			class="table-container"
			:style="{ margin: noScrollNeeded ? '0 auto': '' }">
                <!-- Table -->
                <table-generic
                :tableIdentifier="tableIdentifier"
				:noBoxStyles="noBoxStyles"
				:hasDetail="false"
                :rowType="rowType"
				:rowHeightClass="rowHeightClass"
                :headerType="headerType"
                :headerData="headerData"
                :tableData="tableData"
                :tableStyles="tableStyles"
                :hasMore="hasMore"
                :loadMoreText="loadMoreText"
                :loadingMoreText="loadingMoreText"
                :isLoadingMore="isLoadingMore"
                :alwaysBehaveLikeMobile="alwaysBehaveLikeMobile"
				@rowClicked="$emit('rowClicked', $event)"
				@closeRowClicked="emit('closeRowClicked', $event)"
				@moreOptionsClicked="$emit('moreOptionsClicked', $event)"
				@selectedRadio="emit('selectedRadio', $event)"
				@toggleSwitch="emit('toggleSwitch', $event)"
				@dropdownClicked="emit('dropdownClicked', $event)"
				@onBlur="emit('onBlur', $event)"
				@inputChanged="emit('inputChanged', $event)"
				@loadMore="emit('loadMore')"
				@buttonClicked="$event.dataAttr === 'rowDetailButton' ? $emit('rowClicked', $event) : emit('buttonClicked', $event)"/>
            </div>
        </div>
		<div :id="`teleport-dropdown-target${tableIdentifier ? `-${tableIdentifier}` : ''}`" ref="teleportDropdownTargetRef"></div>
        <!-- Fake scroll to handle table -->
        <div
        v-if="!noScrollNeeded"
        ref="fakeScroll"
        class="fake-scroll-wrapper"
        @scroll="scrollHandler">
            <div class="fake-scroll" :style="{'width': `${tableWidth}px`}"/>
        </div>
        <!-- *********************************************
		*                  Floting Scroll                *
		********************************************** -->
		<teleport to='#side-modal-floating-scroll-teleport' :disabled="!insideDetail">
			<div
			v-if="!noScrollNeeded"
			ref="fakeScrollPortal"
			class="fake-scroll-wrapper inside-portal"
			:key="`renderThisMotherFucker${canRenderFloatPortal}`"
			:class="{'show-me': showPortal && canRenderFloatPortal, 'inside-detail': insideDetail}"
			:style="{'width': `${tableWrappingWidth}px`, left: insideDetail ? null : `${tableWrappingLeft}px`}"
			@scroll="scrollHandlerPortal">
				<div class="fake-scroll" :style="{'width': `${tableWidth}px`}"/>
			</div>
		</teleport>
		<!-- *********************************************
		*                    Add Button                  *
		********************************************** -->
		<button-quaternary v-if="hasAddButton && !addMoreButtonLabel" icon="plus" @clicked="emit('addRow')"/>
        <!-- Secondary Button -->
		<button-secondary
        v-if="hasAddButton && addMoreButtonLabel"
        icon="plus"
        style="margin-top: 16px"
        :label="addMoreButtonLabel"
        @clicked="emit('addRow')"/>
    </div>
</template>

<script setup>
import TableGeneric from '@/components/generic/table/Generic'
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
/* Vue */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { nextTick } from 'process'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* mapActions & mapGetters */
const { getAppScroll, getSideModalScroll } = mapGetters()

/* Emmitters */
const emit = defineEmits(['rowClicked', 'closeRowClicked', 'moreOptionsClicked', 'selectedRadio', 'toggleSwitch',
						'loadMore', 'buttonClicked', 'dropdownClicked', 'inputChanged', 'sortBy', 'addRow', 'onBlur'])

/** Props received:
@tableIdentifier {String} - prop to help for dropdown teleports when we have multiple tables in same feature
@hasAddButton {Boolean} - prop to display add more rows button
@addMoreButtonLabel {String}
@footerIsBeingDisplayed {Boolean} - prop to help calc where our floating scroll should be displayed
@insideDetail {Boolean} - prop to help apply styles and logic for floating scroll
@canRenderFloatPortal {Boolean} - props to trigger display of floating scroll on some portals
@standAlone { Boolean } - prop to apply standAlone class, making our table have its own "box"
@noBoxStyles { Boolean } - prop to remove box styles
@disabled { Boolean } - prop to disabled clicks on table
@noHoverStyles { Boolean } - prop to remove hover styles
@rowType { String } - prop to define row type [generic(default)]
@rowHeightClass { String } - prop to apply class to row, to control it's height: [default height(76px), medium(96px)]
@headerType { String } - prop to define header type [generic(default)]
@headerData { Array } in which each Object is a column in your list with:
	- label [String]: text shown under the step
	- sortable [Boolean]: there are some tables (without pagination) that require a sortable option
	- dataAttr [String, Number]: identifier, used for sortable and to create groups, use (when available) prop provided by MDW for this column
	- showInMobile [Boolean, Object]:
		-[Boolean] if we receive a boolean your cell in mobile will be a column
		-[Object] if we receive an object, your cell will have multiple lines in one column
			key: groupWith, value: dataAttr we want to pair with this cell in mobile layout
@tableData {Array} -
@tableStyles {Array} -
@hasMore {Boolean} -
@loadMoreText {String} -
@loadingMoreText {String} - Text to display while loading more
@isLoadingMore {Boolean} - prop to tell table when to show loader or button
@alwaysBehaveLikeMobile {Boolean} - prop to force mobile layout even in higher resolutions
*/
const props = defineProps({
    tableIdentifier: String,
	hasAddButton: Boolean,
    addMoreButtonLabel: String,
	footerIsBeingDisplayed: Boolean,
    insideDetail: Boolean,
	canRenderFloatPortal: { type: Boolean, default: () => true },
	standAlone: { type: Boolean, default: () => true },
	noBoxStyles: Boolean,
	disabled: Boolean,
	noHoverStyles: Boolean,
	rowType: { type: String, default: () => 'generic' },
	rowHeightClass: String,
	headerType: { type: String, default: () => 'generic' },
	headerData: Array,
	tableData: Array,
	tableStyles: Array,
	hasMore: Boolean,
	loadMoreText: String,
	loadingMoreText: String,
	isLoadingMore: Boolean,
	alwaysBehaveLikeMobile: Boolean
})

/* Helper Data */
const grabing = ref(false)
const noScrollNeeded = ref(false)
const showPortal = ref(false)
const scrollLeft = ref(null)
const tableHeight = ref(0)
const tableWidth = ref(0)
const tableOffsetWidth = ref(0)
const tableWrappingWidth = ref(0)
const tableWrappingLeft = ref(0)
const teleportDropdownTargetRef = ref('')

/* Dom refs */
const tableContainer = ref('')
const tableWrappingRef = ref('')
const tableContainerWrapperRef = ref('')
const fakeScroll = ref('')
const fakeScrollPortal = ref('')

const checkIfTableNeedsScroll = () => {
	tableHeight.value = tableContainer.value.offsetHeight
	tableWidth.value = tableContainer.value.scrollWidth
	tableOffsetWidth.value = tableContainerWrapperRef.value.offsetWidth

    noScrollNeeded.value = tableContainer.value.scrollWidth <= tableWrappingRef.value.offsetWidth
}

const appScroll = computed(() => { return props.insideDetail ? getSideModalScroll.value : getAppScroll.value })
const hasLeftShadow = computed(() => { return scrollLeft.value > 0 })
const hasRightShadow = computed(() => { return scrollLeft.value + tableOffsetWidth.value !== tableWidth.value })

/*************************************************
*                                                *
*                  Scroll Helpers                *
*                                                *
*************************************************/
const updatingScrollPortalHelper = () => {
    let showPortalTemp = false
    const stepPaddingBottom = (props.footerIsBeingDisplayed ? 48 : 0) + 10
    /* finding data related with our table */
    const tablePaddingTopAndHeaderHeight = 16 + 18 + 50 // 15 is a magic number xD
    let distanceToTop = 0
    let tableIsBelowScrollContent = true

    if (tableWrappingRef.value) {
        distanceToTop = tableWrappingRef.value.getBoundingClientRect().top
        tableIsBelowScrollContent = distanceToTop > (window.innerHeight - stepPaddingBottom - tablePaddingTopAndHeaderHeight)
    }
    /* finding data for our fake scroll */
    let fakeScrollDistanceToTop = 0
    let fakeScrollBelowContent = true

    if (fakeScroll.value) {
        fakeScrollDistanceToTop = fakeScroll.value.getBoundingClientRect().top
        fakeScrollBelowContent = fakeScrollDistanceToTop > (window.innerHeight - stepPaddingBottom)
    }
    /* Checking our position and showing portal scroll if needed */
    if (!tableIsBelowScrollContent && fakeScrollBelowContent) showPortalTemp = true
    showPortal.value = showPortalTemp
}

const trueScrollHandler = ($event) => {
    if (fakeScrollPortal.value) fakeScrollPortal.value.scrollLeft = $event.target.scrollLeft
    if (fakeScrollPortal.value) fakeScroll.value.scrollLeft = $event.target.scrollLeft
    scrollLeft.value = tableContainerWrapperRef.value.scrollLeft
    fixDropdowns()
}

const scrollHandler = ($event) => {
    if (tableContainerWrapperRef.value) tableContainerWrapperRef.value.scrollLeft = $event.target.scrollLeft
    if (fakeScrollPortal.value) fakeScrollPortal.value.scrollLeft = $event.target.scrollLeft
    scrollLeft.value = tableContainerWrapperRef.value.scrollLeft
}

const scrollHandlerPortal = ($event) => {
    if (tableContainerWrapperRef.value) tableContainerWrapperRef.value.scrollLeft = $event.target.scrollLeft
    if (fakeScrollPortal.value) fakeScroll.value.scrollLeft = $event.target.scrollLeft
    scrollLeft.value = tableContainerWrapperRef.value.scrollLeft
}

/*************************************************
*                                                *
*                     WATCHERS                   *
*                                                *
*************************************************/
watch(appScroll, (newval, oldVal) => {
    updatingScrollPortalHelper()
})

watch(() => props.canRenderFloatPortal, (newval, oldVal) => {
	nextTick(() => {
		initialSetups()
    })
})

watch(() => props.tableData && props.tableData.length, (newval, oldVal) => {
    setTimeout(() => {
        tableHeight.value = tableContainerWrapperRef.value.offsetHeight
    }, 0)
})

/*************************************************
*                                                *
*                    Mount/Unmount               *
*                                                *
*************************************************/
const initialSetups = () => {
    tableHeight.value = tableContainerWrapperRef.value.offsetHeight
	tableWidth.value = tableContainer.value.offsetWidth
	tableOffsetWidth.value = tableContainerWrapperRef.value.offsetWidth
	tableWrappingWidth.value = tableWrappingRef.value.offsetWidth - 32 // margin
	tableWrappingLeft.value = tableWrappingRef.value.getBoundingClientRect().left

	checkIfTableNeedsScroll()
}

let observer

const fixDropdowns = () => {
	teleportDropdownTargetRef.value.click()
}

onMounted(() => {
	nextTick(() => {
		initialSetups()
        window.addEventListener('resize', initialSetups)

		/* We need to force this MutationObserver to make sure we catch changes in our table DOM */
		const target = document.getElementsByClassName('table-container')[0]
		observer = new MutationObserver(initialSetups)
		const observerOptions = {
			childList: true,
			attributes: true,
			// Omit (or set to false) to observe only changes to the parent node
			subtree: true
		}
		observer.observe(target, observerOptions)
    })
})

onUnmounted(() => {
    window.removeEventListener('resize', initialSetups)
	observer.disconnect()
})
</script>

<style lang="scss" scoped>
.wrapping {
	width: 100%;
	position: relative;

	.shadow-left, .shadow-right {
		width: 32px;

		position: absolute;
		top: 0;
		z-index: 10;

		pointer-events: none;

		&.inside-detail {
			z-index: 30;
			opacity: 0.1;
		}
	}

	.shadow-left {
		left: 0;
		background: var(--gradient-left-table-fade);
	}

	.shadow-right {
		right: 0;
		background: var(--gradient-right-table-fade);
	}

	.table-wrapper-for-scroll {
		width: 100%;
        margin-bottom: 16px;

		position: relative;
		overflow-x: scroll;
		z-index: 1;

		&.grabing {
			cursor: grabbing;
			cursor: -webkit-grabbing;

			.table-container {
				pointer-events: none;
			}
		}

		.table-container {
			width: fit-content;
			overflow: hidden;

			.table-bed {
				width: fit-content;
				max-width: unset;
			}
		}
	}

	.button-elevated {
		min-width: unset;
		margin: 12px 0 24px 16px;
		padding: 0 16px;
	}
}

.fake-scroll-wrapper {
	width: calc(100% - 32px);
	height: 14px;
	border-radius: 4px;
	margin: 0 16px;

    @include horizontal-generic-scrollbar(transparent, var(--primary));

	&.grabing {
		cursor: grabbing;
		cursor: -webkit-grabbing;
	}

	&.inside-portal {
		position: fixed;
		bottom: 92px;
		opacity: 0;
		pointer-events: none;

		&.without-bottom-bar {
			bottom: 12px;
		}

		&.inside-detail {
			z-index: 9001;
			right: 8px;
		}

		&.show-me {
			opacity: 1;
			pointer-events: all;
			z-index: 8999;
		}
	}

	.fake-scroll {
		height: 5px;
	}
}

.table-wrapper-for-scroll {
	-ms-overflow-style: none;  /* Internet Explorer 10+ */
	scrollbar-width: none;  /* Firefox */
}

.table-wrapper-for-scroll::-webkit-scrollbar {
	display: none;  /* Safari and Chrome */
}
</style>
